import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ProductImageGallery from "@/components/ProductImageGallery.jsx";
import ConfirmDialog from "@/components/ConfirmDialog.jsx";
import RowActionsMenu from "@/components/RowActionsMenu.jsx";
import BackLink from "@/components/BackLink.jsx";
import ShareIcon from "@/components/ShareIcon.jsx";
import { cancelOrder, downloadOrderReceipt, getAddresses, getOrder, getProductReviewEligibility, updateOrder } from "@/api/customer.api.js";
import ProductReviewForm from "@/components/ProductReviewForm.jsx";
import { searchProducts } from "@/api/public.api.js";
import { useStoreInfo } from "@/store/StoreInfoContext.jsx";
import { CUSTOMER_PROFILE_DEFAULT_SECTION } from "@/utils/profileMenus.js";
import { CUSTOMER_PROFILE_ROUTE, STORE_NAME } from "@/utils/constants.js";
import {
    canCustomerModifyOrder,
    canDownloadOrderReceipt,
    formatFulfillmentType,
    formatOrderStatus,
    formatPurchaseDate,
    orderStatusClassName
} from "@/utils/format.js";
import { shareOrder } from "@/utils/shareOrder.js";
import Price from "@/components/Price.jsx";
import OrderProgressTracker from "@/components/OrderProgressTracker.jsx";
import { formatShopOptionLabel, getActiveShops } from "@/utils/storeAddress.js";
import { EMPTY_PRODUCT_FILTERS } from "@/utils/productFilters.js";
import { lowestProductPrice } from "@/utils/productPricing.js";
import "@/pages/customer-profile/profile.css";
import "@/pages/product/order.css";
import "@/components/order-progress.css";

function mapLinesToForm(lines) {
    return (lines || []).map((line) => ({
        productId: line.productId,
        title: line.title || "Produit",
        quantity: line.quantity || 1,
        unitPrice: line.unitPrice ?? 0,
        photoIds: line.photoIds?.length ? line.photoIds : (line.photoId ? [line.photoId] : [])
    }));
}

export default function OrderDetailPage() {
    const { orderId } = useParams();
    const { storeInfo } = useStoreInfo();
    const [order, setOrder] = useState(null);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [downloadingReceipt, setDownloadingReceipt] = useState(false);
    const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
    const [addresses, setAddresses] = useState([]);
    const [form, setForm] = useState(null);
    const [productQuery, setProductQuery] = useState("");
    const [productResults, setProductResults] = useState([]);
    const [productSearchLoading, setProductSearchLoading] = useState(false);
    const [reviewProduct, setReviewProduct] = useState(null);
    const [reviewEligibilityByProduct, setReviewEligibilityByProduct] = useState({});
    const [reviewMessage, setReviewMessage] = useState("");
    const [shareFeedback, setShareFeedback] = useState("");

    const activeShops = getActiveShops(storeInfo?.shops);
    const canModify = canCustomerModifyOrder(order?.status);
    const canDownloadReceipt = canDownloadOrderReceipt(order, {
        customerDownloadEnabled: storeInfo?.customerReceiptDownloadEnabled !== false
    });
    const editTotal = useMemo(
        () => (form?.lines || []).reduce(
            (sum, line) => sum + Number(line.unitPrice || 0) * Number(line.quantity || 0),
            0
        ),
        [form]
    );

    useEffect(() => {
        getOrder(orderId)
            .then((data) => setOrder(data.data))
            .catch((err) => setError(err.message));
    }, [orderId]);

    useEffect(() => {
        if (!order?.orderDate) {
            return undefined;
        }
        const when = formatPurchaseDate(order.orderDate);
        document.title = when
            ? `Commande du ${when} — ${STORE_NAME}`
            : `Commande — ${STORE_NAME}`;
        return () => {
            document.title = STORE_NAME;
        };
    }, [order?.orderDate]);

    useEffect(() => {
        setShareFeedback("");
    }, [orderId]);

    useEffect(() => {
        if (!order || order.status !== "COMPLETED" || !order.lines?.length) {
            return undefined;
        }
        let cancelled = false;
        Promise.all(
            order.lines.map(async (line) => {
                try {
                    const response = await getProductReviewEligibility(line.productId);
                    return [line.productId, response.data];
                } catch {
                    return [line.productId, { canReview: false, alreadyReviewed: false }];
                }
            })
        ).then((entries) => {
            if (!cancelled) {
                setReviewEligibilityByProduct(Object.fromEntries(entries));
            }
        });
        return () => {
            cancelled = true;
        };
    }, [order]);

    useEffect(() => {
        if (!editing) {
            return undefined;
        }
        getAddresses()
            .then((data) => setAddresses(data.data || []))
            .catch(() => setAddresses([]));
        return undefined;
    }, [editing]);

    useEffect(() => {
        if (!editing) {
            setProductQuery("");
            setProductResults([]);
            return undefined;
        }
        const query = productQuery.trim();
        if (query.length < 2) {
            setProductResults([]);
            return undefined;
        }
        const timer = setTimeout(() => {
            setProductSearchLoading(true);
            searchProducts({ ...EMPTY_PRODUCT_FILTERS, search: query, stock: "IN_STOCK" }, 0)
                .then((response) => setProductResults(response.data || []))
                .catch(() => setProductResults([]))
                .finally(() => setProductSearchLoading(false));
        }, 350);
        return () => clearTimeout(timer);
    }, [productQuery, editing]);

    const startEdit = () => {
        if (!order || !canModify) {
            return;
        }
        setForm({
            fulfillmentType: order.fulfillmentType || "PICKUP",
            addressId: order.addressId || "",
            shopLocationId: order.shopLocationId || (activeShops[0]?.id || ""),
            customerComment: order.customerComment || "",
            lines: mapLinesToForm(order.lines)
        });
        setMessage("");
        setError("");
        setEditing(true);
    };

    const cancelEdit = () => {
        setEditing(false);
        setForm(null);
        setError("");
    };

    const saveEdit = async (event) => {
        event.preventDefault();
        if (!form || !order) {
            return;
        }
        if (!form.lines.length) {
            setError("La commande doit contenir au moins un article");
            return;
        }
        if (form.lines.some((line) => {
            const qty = Number(line.quantity);
            return !Number.isFinite(qty) || qty < 1;
        })) {
            setError("Chaque article doit avoir une quantité d'au moins 1");
            return;
        }
        if (form.fulfillmentType === "DELIVERY" && !form.addressId) {
            setError("Veuillez sélectionner une adresse ou en ajouter une dans votre compte");
            return;
        }
        if (form.fulfillmentType === "PICKUP" && !form.shopLocationId) {
            setError("Veuillez sélectionner un magasin pour le retrait");
            return;
        }
        setSaving(true);
        setMessage("");
        setError("");
        try {
            const response = await updateOrder(order.orderId, {
                fulfillmentType: form.fulfillmentType,
                addressId: form.fulfillmentType === "DELIVERY" ? form.addressId : undefined,
                shopLocationId: form.fulfillmentType === "PICKUP" ? form.shopLocationId : undefined,
                customerComment: form.customerComment.trim() || undefined,
                lines: form.lines.map((line) => ({
                    productId: line.productId,
                    quantity: Number(line.quantity)
                }))
            });
            setOrder(response.data);
            setEditing(false);
            setForm(null);
            setMessage("Commande mise à jour");
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDownloadReceipt = async () => {
        if (!order?.orderId) {
            return;
        }
        setDownloadingReceipt(true);
        setError("");
        try {
            await downloadOrderReceipt(order.orderId);
            setMessage("Téléchargement du reçu démarré");
        } catch (err) {
            setError(err.message);
        } finally {
            setDownloadingReceipt(false);
        }
    };

    const handleCancelOrder = async () => {
        if (!order) {
            return;
        }
        setCancelConfirmOpen(false);
        setSaving(true);
        setMessage("");
        setError("");
        try {
            const response = await cancelOrder(order.orderId);
            setOrder(response.data);
            setEditing(false);
            setForm(null);
            setMessage("Commande annulée");
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const updateLineQuantity = (productId, raw) => {
        if (raw === "") {
            setForm((prev) => ({
                ...prev,
                lines: prev.lines.map((line) => (
                    line.productId === productId ? { ...line, quantity: "" } : line
                ))
            }));
            return;
        }
        if (!/^\d+$/.test(raw)) {
            return;
        }
        setForm((prev) => ({
            ...prev,
            lines: prev.lines.map((line) => (
                line.productId === productId ? { ...line, quantity: Number(raw) } : line
            ))
        }));
    };

    const commitLineQuantity = (productId) => {
        setForm((prev) => ({
            ...prev,
            lines: prev.lines.map((line) => {
                if (line.productId !== productId) {
                    return line;
                }
                const parsed = Number(line.quantity);
                return {
                    ...line,
                    quantity: Number.isFinite(parsed) && parsed >= 1 ? Math.floor(parsed) : 1
                };
            })
        }));
    };

    const removeLine = (productId) => {
        setForm((prev) => {
            if (prev.lines.length <= 1) {
                setError("La commande doit contenir au moins un article");
                return prev;
            }
            setError("");
            return {
                ...prev,
                lines: prev.lines.filter((line) => line.productId !== productId)
            };
        });
    };

    const addProduct = (product) => {
        if (!product?.id) {
            return;
        }
        setForm((prev) => {
            if (prev.lines.some((line) => line.productId === product.id)) {
                setError("Ce produit est déjà dans la commande");
                return prev;
            }
            setError("");
            return {
                ...prev,
                lines: [
                    ...prev.lines,
                    {
                        productId: product.id,
                        title: product.title || "Produit",
                        quantity: 1,
                        unitPrice: lowestProductPrice(product),
                        photoIds: product.photoIds?.length
                            ? product.photoIds
                            : (product.photoId ? [product.photoId] : [])
                    }
                ]
            };
        });
        setProductQuery("");
        setProductResults([]);
    };

    if (!order && !error) {
        return <p className="container muted">Chargement de la commande...</p>;
    }

    const handleShareOrder = async () => {
        if (!order) {
            return;
        }
        setShareFeedback("");
        try {
            const result = await shareOrder({
                orderId: order.orderId || orderId,
                orderDate: order.orderDate
            });
            if (result === "copied") {
                setShareFeedback("Lien copié");
            }
        } catch {
            setShareFeedback("Partage indisponible");
        }
    };

    const orderMenuItems = [];
    if (order && canDownloadReceipt) {
        orderMenuItems.push({
            id: "download-receipt",
            label: downloadingReceipt ? "Téléchargement…" : "Télécharger le reçu (PDF)",
            onClick: () => {
                if (!downloadingReceipt) {
                    handleDownloadReceipt();
                }
            }
        });
    }
    if (order && canModify) {
        orderMenuItems.push(
            {
                id: "edit",
                label: "Modifier",
                onClick: () => {
                    if (!saving) {
                        startEdit();
                    }
                }
            },
            {
                id: "cancel",
                label: "Annuler la commande",
                danger: true,
                onClick: () => {
                    if (!saving) {
                        setCancelConfirmOpen(true);
                    }
                }
            }
        );
    }

    return (
        <main className="container purchase-detail-page">
            <div className="purchase-detail-toolbar">
                <BackLink
                    to={`${CUSTOMER_PROFILE_ROUTE}?section=${CUSTOMER_PROFILE_DEFAULT_SECTION}`}
                    className="purchase-detail-back"
                >
                    Retour à Mes achats
                </BackLink>
                {order && (
                    <div className="purchase-detail-toolbar-actions">
                        <div className="purchase-detail-share-wrap">
                            <button
                                type="button"
                                className="btn btn-secondary purchase-detail-share"
                                onClick={handleShareOrder}
                                aria-label="Partager la commande"
                            >
                                <span className="purchase-detail-action-label">Partager</span>
                                <ShareIcon className="purchase-detail-share-icon" />
                            </button>
                            {shareFeedback && (
                                <span className="purchase-detail-share-feedback muted" role="status">
                                    {shareFeedback}
                                </span>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {order && !editing && (
                <article className="card purchase-detail-card">
                    <div className="purchase-detail-progress">
                        <OrderProgressTracker order={order} variant="detail" />
                    </div>

                    <header className="purchase-detail-header section-header">
                        <div className="purchase-detail-meta">
                            <p className="muted purchase-detail-date">{formatPurchaseDate(order.orderDate)}</p>
                            <p className="muted purchase-detail-item-count">
                                {order.itemCount} article{order.itemCount > 1 ? "s" : ""}
                            </p>
                        </div>
                        {orderMenuItems.length > 0 && (
                            <RowActionsMenu
                                ariaLabel="Actions de la commande"
                                items={orderMenuItems}
                            />
                        )}
                    </header>

                    {message && <p className="success">{message}</p>}

                    <dl className="purchase-detail-summary">
                        <div className="purchase-detail-row">
                            <dt>Montant total</dt>
                            <dd><strong><Price value={order.totalPrice} /></strong></dd>
                        </div>
                        <div className="purchase-detail-row">
                            <dt>Mode de réception</dt>
                            <dd>{formatFulfillmentType(order.fulfillmentType)}</dd>
                        </div>
                        {order.deliverySummary && (
                            <div className="purchase-detail-row">
                                <dt>{order.fulfillmentType === "DELIVERY" ? "Adresse" : "Lieu"}</dt>
                                <dd>{order.deliverySummary}</dd>
                            </div>
                        )}
                        {order.customerComment && (
                            <div className="purchase-detail-row">
                                <dt>Commentaire</dt>
                                <dd>{order.customerComment}</dd>
                            </div>
                        )}
                        {order.receiptNumber && (
                            <div className="purchase-detail-row">
                                <dt>N° de reçu</dt>
                                <dd>{order.receiptNumber}</dd>
                            </div>
                        )}
                    </dl>

                    <div className="order-lines">
                        {order.lines?.map((line) => (
                            <section key={line.lineId} className="order-line-card">
                                <ProductImageGallery
                                    className="product-gallery--compact"
                                    photoIds={line.photoIds?.length ? line.photoIds : (line.photoId ? [line.photoId] : [])}
                                    title={line.title}
                                    placeholder="Aucune image"
                                />
                                <div>
                                    <h2>{line.title}</h2>
                                    <p className="muted">{line.category}</p>
                                    {line.description && <p>{line.description}</p>}
                                    <p>
                                        {line.quantity} × <Price value={line.unitPrice} /> ={" "}
                                        <strong><Price unitPrice={line.unitPrice} quantity={line.quantity} /></strong>
                                    </p>
                                    <div className="profile-form-actions">
                                        <Link to={`/product/${line.productId}`} className="btn btn-secondary btn-inline">
                                            Voir le produit
                                        </Link>
                                        {order.status === "COMPLETED" && reviewEligibilityByProduct[line.productId]?.canReview && (
                                            <button
                                                type="button"
                                                className="btn btn-primary btn-inline"
                                                onClick={() => {
                                                    setReviewMessage("");
                                                    setReviewProduct({
                                                        productId: line.productId,
                                                        title: line.title
                                                    });
                                                }}
                                            >
                                                Laisser un avis
                                            </button>
                                        )}
                                        {order.status === "COMPLETED"
                                            && reviewEligibilityByProduct[line.productId]?.alreadyReviewed && (
                                            <span className="muted">Avis déjà publié</span>
                                        )}
                                    </div>
                                </div>
                            </section>
                        ))}
                    </div>
                    {reviewProduct && (
                        <div className="card" style={{ marginTop: "1rem", padding: "1rem" }}>
                            {reviewMessage && <p className="success">{reviewMessage}</p>}
                            <ProductReviewForm
                                productId={reviewProduct.productId}
                                productTitle={reviewProduct.title}
                                onCancel={() => setReviewProduct(null)}
                                onDone={() => {
                                    setReviewMessage("Merci, votre avis a été publié.");
                                    setReviewEligibilityByProduct((prev) => ({
                                        ...prev,
                                        [reviewProduct.productId]: {
                                            canReview: false,
                                            alreadyReviewed: true
                                        }
                                    }));
                                    setTimeout(() => setReviewProduct(null), 1200);
                                }}
                            />
                        </div>
                    )}
                </article>
            )}

            {order && editing && form && (
                <article className="card purchase-detail-card">
                    <header className="purchase-detail-header">
                        <h1>Modifier la commande</h1>
                        <p className="muted">
                            Statut :{" "}
                            <span className={orderStatusClassName(order.status)}>
                                {formatOrderStatus(order.status)}
                            </span>
                        </p>
                    </header>

                    <form onSubmit={saveEdit}>
                        <fieldset className="order-fulfillment">
                            <legend>Mode de réception</legend>
                            <label className="order-fulfillment-option">
                                <input
                                    type="radio"
                                    name="fulfillmentType"
                                    value="PICKUP"
                                    checked={form.fulfillmentType === "PICKUP"}
                                    onChange={() => setForm({ ...form, fulfillmentType: "PICKUP" })}
                                    disabled={saving}
                                />
                                <span>
                                    <strong>Retrait en magasin</strong>
                                    <span className="muted order-fulfillment-detail">
                                        Retirer la commande dans l&apos;un de nos magasins
                                    </span>
                                </span>
                            </label>
                            <label className="order-fulfillment-option">
                                <input
                                    type="radio"
                                    name="fulfillmentType"
                                    value="DELIVERY"
                                    checked={form.fulfillmentType === "DELIVERY"}
                                    onChange={() => setForm({ ...form, fulfillmentType: "DELIVERY" })}
                                    disabled={saving}
                                />
                                <span>
                                    <strong>Livraison à l&apos;adresse</strong>
                                    <span className="muted order-fulfillment-detail">
                                        Garage, atelier ou autre adresse de votre choix.
                                    </span>
                                </span>
                            </label>
                        </fieldset>

                        {form.fulfillmentType === "PICKUP" && (
                            <label className="field">
                                Magasin de retrait
                                <select
                                    value={form.shopLocationId}
                                    onChange={(e) => setForm({ ...form, shopLocationId: e.target.value })}
                                    disabled={saving || activeShops.length === 0}
                                    required
                                >
                                    {activeShops.map((shop) => (
                                        <option key={shop.id} value={shop.id}>
                                            {formatShopOptionLabel(shop)}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        )}

                        {form.fulfillmentType === "DELIVERY" && (
                            addresses.length === 0 ? (
                                <div className="order-address-empty">
                                    <p className="muted">
                                        Aucune adresse enregistrée. Ajoutez-en une dans votre compte pour
                                        recevoir la commande (garage, atelier, etc.).
                                    </p>
                                    <Link
                                        to={`${CUSTOMER_PROFILE_ROUTE}?section=addresses`}
                                        className="btn btn-secondary btn-inline"
                                    >
                                        Ajouter une adresse dans mon compte
                                    </Link>
                                </div>
                            ) : (
                                <label className="field">
                                    Adresse de livraison
                                    <select
                                        value={form.addressId}
                                        onChange={(e) => setForm({ ...form, addressId: e.target.value })}
                                        disabled={saving}
                                        required
                                    >
                                        {addresses.map((address) => (
                                            <option key={address.id} value={address.id}>
                                                {(address.label ? `${address.label} — ` : "") + address.summary}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            )
                        )}

                        <label className="field">
                            Commentaire (optionnel)
                            <textarea
                                rows="3"
                                value={form.customerComment}
                                onChange={(e) => setForm({ ...form, customerComment: e.target.value })}
                                maxLength={1000}
                                disabled={saving}
                            />
                        </label>

                        <h2>Articles</h2>
                        <p className="muted">Total indicatif : <Price value={editTotal} /></p>
                        <div className="order-lines">
                            {form.lines.map((line) => (
                                <section key={line.productId} className="order-line-card">
                                    <ProductImageGallery
                                        className="product-gallery--compact"
                                        photoIds={line.photoIds}
                                        title={line.title}
                                        placeholder="Aucune image"
                                    />
                                    <div>
                                        <h3>{line.title}</h3>
                                        <p className="muted"><Price value={line.unitPrice} /> / unité</p>
                                        <div className="order-line-qty-row">
                                            <label className="field field--qty">
                                                Quantité
                                                <input
                                                    type="number"
                                                    min="1"
                                                    step="1"
                                                    inputMode="numeric"
                                                    value={line.quantity}
                                                    onChange={(e) => updateLineQuantity(line.productId, e.target.value)}
                                                    onBlur={() => commitLineQuantity(line.productId)}
                                                    disabled={saving}
                                                    required
                                                />
                                            </label>
                                            <p className="order-line-subtotal">
                                                Sous-total :{" "}
                                                <strong><Price unitPrice={line.unitPrice} quantity={line.quantity} /></strong>
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-secondary btn-inline"
                                            onClick={() => removeLine(line.productId)}
                                            disabled={saving || form.lines.length <= 1}
                                        >
                                            Retirer
                                        </button>
                                    </div>
                                </section>
                            ))}
                        </div>

                        <label className="field">
                            Ajouter un produit
                            <input
                                type="search"
                                value={productQuery}
                                onChange={(e) => setProductQuery(e.target.value)}
                                placeholder="Rechercher un produit (2 caractères min.)"
                                autoComplete="off"
                                disabled={saving}
                            />
                        </label>
                        {productSearchLoading && <p className="muted">Recherche...</p>}
                        {!productSearchLoading && productResults.length > 0 && (
                            <ul className="admin-order-product-results">
                                {productResults.map((product) => (
                                    <li key={product.id}>
                                        <button
                                            type="button"
                                            className="admin-order-product-result"
                                            onClick={() => addProduct(product)}
                                            disabled={saving}
                                        >
                                            <span>{product.title}</span>
                                            <span className="muted"><Price value={lowestProductPrice(product)} /></span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}

                        <div className="profile-form-actions">
                            <button
                                className="btn btn-primary btn-inline"
                                type="submit"
                                disabled={
                                    saving
                                    || (form.fulfillmentType === "DELIVERY" && addresses.length === 0)
                                    || (form.fulfillmentType === "PICKUP" && activeShops.length === 0)
                                }
                            >
                                {saving ? "Enregistrement..." : "Enregistrer"}
                            </button>
                            <button
                                type="button"
                                className="btn btn-secondary btn-inline"
                                onClick={cancelEdit}
                                disabled={saving}
                            >
                                Retour
                            </button>
                        </div>
                    </form>
                </article>
            )}

            {error && <p className="error">{error}</p>}

            <ConfirmDialog
                open={cancelConfirmOpen}
                title="Annuler la commande"
                message="Voulez-vous vraiment annuler cette commande ?"
                confirmLabel="Annuler la commande"
                dangerous
                onConfirm={handleCancelOrder}
                onCancel={() => setCancelConfirmOpen(false)}
            />
        </main>
    );
}
